import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAlert, useConfirm } from "../components/Confirm";
import { formatDate } from "../lib/format";

interface RoleRow {
  key: string;
  name: string;
  description: string | null;
  sort_order: number;
  active: boolean;
  entra_group_id: string | null;
  entra_group_name: string | null;
  modules: string[];
}

interface GroupChoice {
  id: string | null;
  name: string | null;
}

interface UserRow {
  user_oid: string;
  user_email: string;
  user_display_name: string | null;
  role_key: string | null;
  modules: string[];
  auto_synced: boolean;
  granted_by_email: string | null;
  granted_at: string;
  updated_at: string;
}

export function Users() {
  const [tab, setTab] = useState<"users" | "roles">("users");
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const isAdmin = !!me.data?.roles?.admin;

  if (me.isLoading) return <div className="text-sm text-slate-500">Loading...</div>;
  if (!isAdmin) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-600">
        Only admins can manage users and roles.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-liaise-navy">Users &amp; access</h1>
          <p className="text-sm text-slate-500">Who can use which pricing modules. Roles are templates; the modules list is the source of truth per user.</p>
        </div>
        <nav className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {(["users", "roles"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-sm px-4 py-1.5 rounded-md ${
                tab === t ? "bg-white text-liaise-navy shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t === "users" ? "Users" : "Roles & permissions"}
            </button>
          ))}
        </nav>
      </div>
      {tab === "users" ? <UsersTab /> : <RolesTab />}
    </div>
  );
}

/* ============================== Users tab ============================== */

function UsersTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["users-access"], queryFn: api.usersAccessList });
  const roles = useQuery({ queryKey: ["roles"], queryFn: api.rolesList });
  const modules = useQuery({ queryKey: ["modules-admin"], queryFn: api.modulesAdmin });
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [syncing, setSyncing] = useState(false);
  const confirm = useConfirm();
  const alertModal = useAlert();

  async function onSyncFromGroups() {
    setSyncing(true);
    try {
      const res = await api.syncUsersFromGroups();
      await qc.invalidateQueries({ queryKey: ["users-access"] });
      const rows: { role_key: string; group_name: string | null; members_total: number; provisioned: number; skipped_explicit: number }[] = res?.perRole ?? [];
      const total = res?.total_provisioned ?? 0;
      const lines = rows.map((r) => `${r.role_key} (${r.group_name ?? "?"}): ${r.provisioned} added/updated, ${r.skipped_explicit} kept (admin-set)`).join("\n");
      await alertModal({ title: `Synced ${total} users from groups`, body: lines || "No linked groups found." });
    } catch (err) {
      await alertModal({ title: "Sync failed", body: String(err) });
    } finally {
      setSyncing(false);
    }
  }

  const users: UserRow[] = list.data?.users ?? [];
  const roleList: RoleRow[] = roles.data?.roles ?? [];
  const moduleList: { key: string; display_name: string; status: string }[] =
    (modules.data?.modules ?? []).filter((m: any) => m.key !== "system");

  async function onDelete(u: UserRow) {
    const ok = await confirm({
      title: "Revoke access?",
      body: `${u.user_display_name ?? u.user_email} will no longer see any pricing modules until they're re-added.`,
      danger: true,
      confirmLabel: "Revoke",
    });
    if (!ok) return;
    try {
      await api.userAccessDelete(u.user_oid);
      await qc.invalidateQueries({ queryKey: ["users-access"] });
    } catch (err) {
      await alertModal({ title: "Couldn't revoke", body: String(err) });
    }
  }

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
          <div className="text-sm text-slate-500">
            {users.length} user{users.length === 1 ? "" : "s"} with access
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onSyncFromGroups}
              disabled={syncing}
              className={`text-sm px-3 py-1.5 rounded-lg border ${
                syncing
                  ? "border-slate-200 text-slate-400 cursor-not-allowed"
                  : "border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
              title="Pulls all members of every linked Entra group into the users list"
            >
              {syncing ? "Syncing..." : "↻ Sync from groups"}
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="text-sm bg-liaise-blue text-white px-3 py-1.5 rounded-lg hover:bg-blue-700"
            >
              + Add user
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">User</th>
              <th className="text-left px-4 py-2.5 font-medium">Role</th>
              <th className="text-left px-4 py-2.5 font-medium">Modules</th>
              <th className="text-left px-4 py-2.5 font-medium">Last updated</th>
              <th className="text-right px-4 py-2.5 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_oid} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-liaise-navy">{u.user_display_name ?? u.user_email}</div>
                  <div className="text-xs text-slate-500">{u.user_email}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <div className="flex items-center gap-1.5">
                    <span>{u.role_key ? (roleList.find((r) => r.key === u.role_key)?.name ?? u.role_key) : <span className="text-slate-400">Custom</span>}</span>
                    {u.auto_synced ? (
                      <span title="From Entra group" className="text-[10px] uppercase font-medium tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">auto</span>
                    ) : (
                      <span title="Set explicitly by admin" className="text-[10px] uppercase font-medium tracking-wide bg-blue-50 text-liaise-blue px-1.5 py-0.5 rounded">custom</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {u.modules.length === 0 ? (
                      <span className="text-xs text-slate-400">No access</span>
                    ) : u.modules.map((m) => (
                      <span key={m} className="text-xs bg-blue-50 text-liaise-blue px-2 py-0.5 rounded">
                        {moduleList.find((x) => x.key === m)?.display_name ?? m}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{formatDate(u.updated_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => setEditing(u)}
                    className="text-sm text-liaise-blue hover:underline"
                  >Edit</button>
                  <span className="text-slate-300 mx-2">|</span>
                  <button
                    onClick={() => onDelete(u)}
                    className="text-sm text-red-600 hover:underline"
                  >Revoke</button>
                </td>
              </tr>
            ))}
            {!users.length && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400">No users yet. Click "Add user" to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <UserEditor
          mode="add"
          roles={roleList}
          modules={moduleList}
          onClose={() => setShowAdd(false)}
          onSaved={async () => { setShowAdd(false); await qc.invalidateQueries({ queryKey: ["users-access"] }); }}
        />
      )}
      {editing && (
        <UserEditor
          mode="edit"
          existing={editing}
          roles={roleList}
          modules={moduleList}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await qc.invalidateQueries({ queryKey: ["users-access"] }); }}
        />
      )}
    </>
  );
}

interface UserEditorProps {
  mode: "add" | "edit";
  existing?: UserRow;
  roles: RoleRow[];
  modules: { key: string; display_name: string; status: string }[];
  onClose: () => void;
  onSaved: () => void;
}

function UserEditor({ mode, existing, roles, modules, onClose, onSaved }: UserEditorProps) {
  const [pickedUser, setPickedUser] = useState<{
    oid: string; email: string; displayName: string;
  } | null>(
    existing ? {
      oid: existing.user_oid, email: existing.user_email,
      displayName: existing.user_display_name ?? existing.user_email,
    } : null
  );
  const [roleKey, setRoleKey] = useState<string | null>(existing?.role_key ?? null);
  const [moduleKeys, setModuleKeys] = useState<string[]>(existing?.modules ?? []);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const alertModal = useAlert();

  // Debounced search
  useEffect(() => {
    if (mode === "edit") return;
    if (!search.trim() || pickedUser) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.searchUsers(search.trim());
        setResults(r?.users ?? r?.value ?? []);
      } catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, mode, pickedUser]);

  function applyRole(key: string | null) {
    setRoleKey(key);
    if (key) {
      const r = roles.find((x) => x.key === key);
      if (r) setModuleKeys(r.modules);
    }
  }

  function toggleModule(k: string) {
    setModuleKeys((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  }

  async function save() {
    if (!pickedUser) { setError("Pick a user first."); return; }
    setError(null);
    try {
      await api.userAccessUpsert(pickedUser.oid, {
        user_email: pickedUser.email,
        user_display_name: pickedUser.displayName,
        role_key: roleKey,
        modules: moduleKeys,
      });
      onSaved();
    } catch (err) {
      await alertModal({ title: "Couldn't save", body: String(err) });
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-liaise-navy">{mode === "add" ? "Grant access" : "Edit access"}</h2>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* User picker */}
          {mode === "add" ? (
            <div>
              <label className="block text-xs uppercase text-slate-500 font-medium mb-1.5">User</label>
              {pickedUser ? (
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <div>
                    <div className="font-medium text-slate-700">{pickedUser.displayName}</div>
                    <div className="text-xs text-slate-500">{pickedUser.email}</div>
                  </div>
                  <button onClick={() => { setPickedUser(null); setSearch(""); }} className="text-xs text-slate-500 hover:text-slate-700">Change</button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    autoFocus
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-liaise-blue"
                  />
                  {search.trim() && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                      {searching && <div className="px-3 py-2 text-xs text-slate-500">Searching...</div>}
                      {!searching && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No matches.</div>}
                      {results.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => {
                            setPickedUser({
                              oid: u.id,
                              email: u.mail || u.userPrincipalName,
                              displayName: u.displayName || u.userPrincipalName,
                            });
                            setResults([]); setSearch("");
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        >
                          <div className="text-sm font-medium text-liaise-navy">{u.displayName}</div>
                          <div className="text-xs text-slate-500">{u.mail || u.userPrincipalName}{u.jobTitle ? ` · ${u.jobTitle}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="text-xs uppercase text-slate-500 font-medium mb-1.5">User</div>
              <div className="font-medium text-slate-700">{pickedUser?.displayName}</div>
              <div className="text-xs text-slate-500">{pickedUser?.email}</div>
            </div>
          )}

          {/* Role template */}
          <div>
            <label className="block text-xs uppercase text-slate-500 font-medium mb-1.5">Role template (optional)</label>
            <select
              value={roleKey ?? ""}
              onChange={(e) => applyRole(e.target.value || null)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-liaise-blue"
            >
              <option value="">Custom (tick modules manually)</option>
              {roles.map((r) => (
                <option key={r.key} value={r.key}>{r.name}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1.5">Picking a role pre-fills the module list. You can still tick or untick modules afterwards.</p>
          </div>

          {/* Modules */}
          <div>
            <label className="block text-xs uppercase text-slate-500 font-medium mb-1.5">Modules this user can see</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {modules.map((m) => (
                <label key={m.key} className="flex items-start gap-2 px-3 py-2 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={moduleKeys.includes(m.key)}
                    onChange={() => toggleModule(m.key)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-liaise-navy">{m.display_name}</div>
                    <div className="text-xs text-slate-500">{m.status}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={save} className="text-sm px-4 py-1.5 rounded-lg bg-liaise-blue text-white hover:bg-blue-700">Save</button>
        </div>
      </div>
    </div>
  );
}

/* ============================== Roles tab ============================== */

function RolesTab() {
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ["roles"], queryFn: api.rolesList });
  const modules = useQuery({ queryKey: ["modules-admin"], queryFn: api.modulesAdmin });
  const alertModal = useAlert();

  const roleList: RoleRow[] = roles.data?.roles ?? [];
  const moduleList: { key: string; display_name: string }[] =
    (modules.data?.modules ?? []).filter((m: any) => m.key !== "system");

  // Local "draft" state per role so admin can edit checkboxes before saving.
  const [drafts, setDrafts] = useState<Record<string, string[]>>({});
  const [groupDrafts, setGroupDrafts] = useState<Record<string, GroupChoice>>({});
  useEffect(() => {
    const init: Record<string, string[]> = {};
    const initG: Record<string, GroupChoice> = {};
    roleList.forEach((r) => {
      init[r.key] = r.modules;
      initG[r.key] = { id: r.entra_group_id, name: r.entra_group_name };
    });
    setDrafts(init);
    setGroupDrafts(initG);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles.data]);

  const dirtyKeys = useMemo(
    () => roleList.filter((r) =>
      !arraysEqual(drafts[r.key] ?? [], r.modules) ||
      ((groupDrafts[r.key]?.id ?? null) !== (r.entra_group_id ?? null))
    ).map((r) => r.key),
    [drafts, groupDrafts, roleList]
  );

  function toggle(roleKey: string, moduleKey: string) {
    setDrafts((cur) => {
      const list = cur[roleKey] ?? [];
      return { ...cur, [roleKey]: list.includes(moduleKey) ? list.filter((m) => m !== moduleKey) : [...list, moduleKey] };
    });
  }

  async function save(roleKey: string) {
    const role = roleList.find((r) => r.key === roleKey);
    if (!role) return;
    try {
      // Save modules if they changed
      if (!arraysEqual(drafts[roleKey] ?? [], role.modules)) {
        await api.roleModulesUpdate(roleKey, drafts[roleKey] ?? []);
      }
      // Save Entra group binding if it changed
      const draftG = groupDrafts[roleKey] ?? { id: null, name: null };
      if ((draftG.id ?? null) !== (role.entra_group_id ?? null)) {
        await api.roleGroupUpdate(roleKey, draftG.id, draftG.name);
      }
      await qc.invalidateQueries({ queryKey: ["roles"] });
    } catch (err) {
      await alertModal({ title: "Couldn't save", body: String(err) });
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 text-sm text-slate-500">
        Tick a box to set which modules a role grants. Optionally link a role to an Entra security group ID so anyone in that group automatically inherits the role's modules. Per-user overrides on the Users tab always win.
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr>
            <th className="text-left px-4 py-2.5 font-medium">Role</th>
            {moduleList.map((m) => (
              <th key={m.key} className="text-center px-3 py-2.5 font-medium">{m.display_name}</th>
            ))}
            <th className="text-left px-4 py-2.5 font-medium">Entra group</th>
            <th className="text-right px-4 py-2.5 font-medium">Save</th>
          </tr>
        </thead>
        <tbody>
          {roleList.map((r) => (
            <tr key={r.key} className="border-t border-slate-100">
              <td className="px-4 py-3">
                <div className="font-medium text-liaise-navy">{r.name}</div>
                {r.description && <div className="text-xs text-slate-500">{r.description}</div>}
              </td>
              {moduleList.map((m) => (
                <td key={m.key} className="text-center px-3 py-3">
                  <input
                    type="checkbox"
                    checked={(drafts[r.key] ?? []).includes(m.key)}
                    onChange={() => toggle(r.key, m.key)}
                  />
                </td>
              ))}
              <td className="px-4 py-3">
                <GroupPicker
                  value={groupDrafts[r.key] ?? { id: null, name: null }}
                  onChange={(g) => setGroupDrafts((cur) => ({ ...cur, [r.key]: g }))}
                />
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  disabled={!dirtyKeys.includes(r.key)}
                  onClick={() => save(r.key)}
                  className={`text-sm px-3 py-1 rounded-lg ${
                    dirtyKeys.includes(r.key)
                      ? "bg-liaise-blue text-white hover:bg-blue-700"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                >Save</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function arraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

/* ============================== Group picker ============================== */

function GroupPicker({ value, onChange }: { value: GroupChoice; onChange: (g: GroupChoice) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  useEffect(() => {
    if (!open || !search.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.searchGroups(search.trim());
        setResults(r?.groups ?? []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open]);

  if (!open) {
    return (
      <div className="flex items-center gap-2">
        {value.id ? (
          <>
            <button
              type="button"
              onClick={() => { setOpen(true); setSearch(""); }}
              className="text-sm text-left bg-slate-50 border border-slate-200 px-2 py-1 rounded hover:bg-slate-100 max-w-[18rem] truncate"
              title={value.id}
            >
              {value.name ?? value.id}
            </button>
            <button
              type="button"
              onClick={() => onChange({ id: null, name: null })}
              className="text-xs text-slate-400 hover:text-red-600"
              title="Unlink"
            >
              ✕
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => { setOpen(true); setSearch(""); }}
            className="text-sm text-liaise-blue hover:underline"
          >
            + Link group
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        autoFocus
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search groups..."
        className="w-72 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:border-liaise-blue"
      />
      {search.trim() && (
        <div className="absolute z-10 mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {searching && <div className="px-3 py-2 text-xs text-slate-500">Searching...</div>}
          {!searching && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No matches.</div>}
          {results.map((g) => (
            <button
              key={g.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange({ id: g.id, name: g.displayName });
                setOpen(false);
                setSearch("");
                setResults([]);
              }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
            >
              <div className="text-sm font-medium text-liaise-navy">{g.displayName}</div>
              {g.description && <div className="text-xs text-slate-500 truncate">{g.description}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
